import { useState } from "react";
import { FileDown, FolderSync, Lock, Plus } from "lucide-react";
import { ImportServersDialog } from "./ImportServersDialog";
import { CredentialsBoxDialog } from "./CredentialsBoxDialog";
import { RefreshFolderDialog } from "../projects/RefreshFolderDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { ServerForm } from "./ServerForm";
import { ServerRow } from "./ServerRow";
import { useServers } from "../../hooks/useServers";

export function ServersDialog({ open, onOpenChange, project }) {
  const projectId = project?.id;
  const { data: servers, isLoading } = useServers(open ? projectId : null);
  const [formMode, setFormMode] = useState(null); // null | "new" | server (edición)
  const [showImport, setShowImport] = useState(false);
  const [showCredentialsBox, setShowCredentialsBox] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);

  const handleOpenChange = (next) => {
    if (!next) setFormMode(null);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="servers-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Servidores de {project?.name}</DialogTitle>
          <DialogDescription>
            Conecta los servidores de este proyecto una sola vez. La contraseña se guarda cifrada y la IA nunca la ve:
            solo usa la conexión.
          </DialogDescription>
        </DialogHeader>

        {formMode ? (
          <ServerForm
            key={formMode === "new" ? "new" : formMode.id}
            projectId={projectId}
            servers={servers || []}
            server={formMode === "new" ? null : formMode}
            onDone={() => setFormMode(null)}
            onCancel={() => setFormMode(null)}
          />
        ) : (
          <div className="space-y-3">
            {isLoading && <p className="text-sm text-muted-foreground">Cargando servidores...</p>}
            {!isLoading && servers?.length === 0 && (
              <p data-testid="servers-empty" className="text-sm text-muted-foreground">
                Este proyecto todavía no tiene servidores conectados.
              </p>
            )}
            {servers?.map((server) => (
              <ServerRow key={server.id} projectId={projectId} server={server} onEdit={setFormMode} />
            ))}
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid="server-add-button" variant="outline" size="sm" onClick={() => setFormMode("new")}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Conectar servidor
              </Button>
              <Button data-testid="server-paste-button" variant="outline" size="sm" onClick={() => setShowCredentialsBox(true)}>
                <Lock className="h-3.5 w-3.5 mr-1.5" /> Compartir credenciales
              </Button>
              {project?.drive_folder && (
                <Button data-testid="server-refresh-button" variant="outline" size="sm" onClick={() => setShowRefresh(true)}>
                  <FolderSync className="h-3.5 w-3.5 mr-1.5" /> Releer carpeta de Drive
                </Button>
              )}
              <Button data-testid="server-import-button" variant="outline" size="sm" onClick={() => setShowImport(true)}>
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> Importar desde archivo
              </Button>
            </div>
          </div>
        )}
        <ImportServersDialog open={showImport} onOpenChange={setShowImport} project={project} />
        <CredentialsBoxDialog open={showCredentialsBox} onOpenChange={setShowCredentialsBox} project={project} />
        <RefreshFolderDialog open={showRefresh} onOpenChange={setShowRefresh} project={project} />
      </DialogContent>
    </Dialog>
  );
}
